<template>
<div class="wajaran-page">

  <PageHeader
    icon="mdi-scale-balance"
    title="Pengurusan Wajaran"
    subtitle="Pengurusan senarai wajaran mengikut jenis, tahun, pusingan dan negeri"
  />

  <!-- DATATABLE  -->
  <v-card outlined class="mb-4">
    <v-card-title class="py-3 d-flex align-center flex-wrap">
      <v-icon small class="mr-2">mdi-table</v-icon>
      <span class="subtitle-1 font-weight-medium">Senarai Wajaran</span>
      <v-spacer></v-spacer>
      
      <v-btn outlined class="reset-btn text-none mr-2" @click="openExcelDialog">
        <v-icon left>mdi-file-excel</v-icon> Urusan Pukal
      </v-btn>
      <v-btn outlined class="reset-btn text-none mr-2" @click="dialogJanaTahun = true">
        <v-icon left>mdi-calendar-plus</v-icon> Jana Tahun Baru
      </v-btn>
      <v-btn class="btn-teal text-none mr-2" @click="openFormDialog('tambah')">
        <v-icon left>mdi-plus</v-icon> Tambah Rekod
      </v-btn>
      <!-- Admin-only; self-hides for other roles. Selaras nilai wajaran ke HID. -->
      <wajaran-sync-button ref="selarasBtn" />
    </v-card-title>

    <v-card-text class="filter-bar py-3">
      <!-- Filters -->
      <v-row align="end" dense>
        <v-col cols="12" sm="2">
          <div class="fld__label">Jenis</div>
          <v-select v-model="filterJenis" :items="[{text: 'Pendapatan', value: 'I'}, {text: 'Perbelanjaan', value: 'E'}]" outlined dense hide-details></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <div class="fld__label">Tahun</div>
          <v-select v-model="filterTahun" :items="uniqueTahun" outlined dense hide-details prepend-inner-icon="mdi-calendar"></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <div class="fld__label">Pusingan</div>
          <v-select v-model="filterPusingan" :items="uniquePusingan" outlined dense hide-details></v-select>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="fld__label">Negeri</div>
          <v-autocomplete v-model="filterNegeri" :items="listNegeri" :item-text="item => item.negeriID +' - '+ item.negeri" item-value="negeriID" clearable outlined dense hide-details></v-autocomplete>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-btn outlined height="40" class="text-none reset-btn" @click="resetFiltersList">
            <v-icon small left>mdi-refresh</v-icon>
            Tetapkan Semula
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <!-- SUMMARY MATRIX -->
      <v-card v-if="filterTahun && filterJenis" outlined class="mb-4 mt-2">
        <v-card-title class="subtitle-2 font-weight-bold py-2 grey lighten-4" style="cursor: pointer" @click="summaryExpanded = !summaryExpanded">
          Status Kemasukan Data ({{ filterTahun }} - {{ filterJenis === 'I' ? 'Pendapatan' : 'Perbelanjaan' }})
          <v-spacer></v-spacer>
          <v-icon>{{ summaryExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-0" style="max-height: 400px; overflow-y: auto;">
          <v-simple-table dense fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="width: 250px;">Negeri</th>
                  <th v-for="p in uniquePusinganNoSemua" :key="p" class="text-center">P{{ p }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="grey lighten-3 font-weight-bold">
                  <td>JUMLAH NEGERI SELESAI</td>
                  <td v-for="p in uniquePusinganNoSemua" :key="p" class="text-center primary--text">
                    {{ summaryPusinganData[p] || 0 }} / 16
                  </td>
                </tr>
                <template v-if="summaryExpanded">
                  <tr v-for="row in summaryMatrixData" :key="row.negeriID">
                    <td class="text-left font-weight-medium">{{ row.negeriID }} - {{ row.negeriName }}</td>
                    <td v-for="p in uniquePusinganNoSemua" :key="p" class="text-center">
                      <v-icon v-if="row.pusinganData[p]" color="#037769" small>mdi-check-circle</v-icon>
                      <v-icon v-else color="#d9534f" small>mdi-close-circle</v-icon>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <div class="d-flex justify-end mb-2">
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined small class="reset-btn text-none" v-bind="attrs" v-on="on">
              <v-icon left>mdi-view-column</v-icon> Pilihan Kolum
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="header in defaultHeaders" :key="header.value">
              <v-list-item-action>
                <v-checkbox v-model="selectedHeaders" :value="header.value" hide-details dense></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ header.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Baris boleh diklik untuk disunting, tetapi tiada apa dalam jadual
           yang menunjukkannya - tiada butang, tiada ikon pada hujung baris.
           Tanpa nota ini pengguna hanya akan menemuinya secara tidak sengaja. -->
      <div class="panduan-klik mb-2 d-flex justify-end align-center">
        <v-icon small class="mr-2 panduan-klik__ikon">mdi-cursor-default-click-outline</v-icon>
        <span>Klik mana-mana baris dalam jadual untuk mengemas kini nilai wajarannya.</span>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredDataWajaran"
        class="elevation-1 wajaran-table"
        @click:row="handleClick"
        :footer-props="{'items-per-page-options':[10, 30, 50, 100, -1]}"
      >
        <template v-slot:item.Type="{ item }">
          <v-chip :color="item.Type == 'I' ? '#F68F39' : '#00B2FF'" dark small>
            {{ item.Type == 'I' ? 'Pendapatan' : 'Perbelanjaan' }}
          </v-chip>
        </template>
        <template v-slot:item.NG="{ item }">
          {{ getNegeriName(item.NG) }}
        </template>
        <template v-slot:item.DP="{ item }">
          {{ getDPName(item.NG, item.DP) }}
        </template>
        <template v-slot:item.KWKIRB="{ item }">
          {{ item.KWKIRB == '1' ? '1 - Warganegara' : (item.KWKIRB == '2' ? '2 - Bukan Warganegara' : item.KWKIRB) }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <!-- DIALOG TAMBAH/KEMASKINI -->
  <v-dialog v-model="dialogForm" max-width="800px" persistent>
    <v-card>
      <v-card-title class="py-3 kad__tajuk">
        <v-icon small class="mr-2">{{ isEdit ? 'mdi-pencil' : 'mdi-plus-circle-outline' }}</v-icon>
        <span class="subtitle-1 font-weight-medium">
          {{ isEdit ? 'Kemaskini Wajaran' : 'Tambah Wajaran Baru' }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialogForm = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12">
              <v-radio-group v-model='selectedType' row class="mt-0" :disabled="isEdit">
                <template v-slot:label>
                  <div class="mr-4"><strong>Jenis:</strong></div>
                </template>
                <v-radio value="I">
                  <template v-slot:label>
                    <div><strong style="color:#F68F39">Pendapatan</strong></div>
                  </template>
                </v-radio>
                <v-radio value="E">
                  <template v-slot:label>
                    <div><strong style="color:#00B2FF">Perbelanjaan</strong></div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="selectedTahun" label="Tahun" :items="uniqueTahun" outlined dense :rules="[v => !!v || 'Tahun diperlukan']" :disabled="isEdit"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="selectedPusingan" label="Pusingan" :items="uniquePusinganNoSemua" outlined dense :rules="[v => !!v || 'Pusingan diperlukan']" :disabled="isEdit"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete outlined dense label="Negeri" :items="listNegeri" :item-text="item => item.negeriID +' - '+ item.negeri" return-object v-model="selectedNegeri" :rules="[v => !!v || 'Negeri diperlukan']" :disabled="isEdit"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="selectedDP" label="Daerah Pentadbiran (DP)" :items="filteredDPList" :item-text="item => String(item.DP).padStart(2, '0') + ' - ' + item.NamaDP" item-value="DP" outlined dense :rules="[v => !!v || 'DP diperlukan']" :disabled="isEdit || !selectedNegeri"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="selectedStrata" label="Strata" :items="['Bandar', 'Luar Bandar']" outlined dense :rules="[v => !!v || 'Strata diperlukan']" :disabled="isEdit"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="selectedKW" label="Warganegara (KWKIRB)" :items="[{text: '1 - Warganegara', value: '1'}, {text: '2 - Bukan Warganegara', value: '2'}]" outlined dense :rules="[v => !!v || 'KWKIRB diperlukan']" :disabled="isEdit"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="wajaranValue" label="Wajaran" hint="cth: 1.1234" outlined dense type="number" step="0.0001" :rules="[v => !!v || 'Wajaran diperlukan', v => /^\d+(\.\d{1,4})?$/.test(v) || 'Maksimum 4 tempat perpuluhan sahaja']"></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text class="text-none" @click="dialogForm = false">Batal</v-btn>
        <v-btn class="btn-teal text-none" @click="simpanWajaranPendapatan">Simpan</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- DIALOG EXCEL PUKAL -->
  <v-dialog v-model="dialogExcel" max-width="800px" persistent>
    <v-card>
      <v-card-title class="py-3 kad__tajuk">
        <v-icon small class="mr-2">mdi-microsoft-excel</v-icon>
        <span class="subtitle-1 font-weight-medium">Muat Turun &amp; Muat Naik Excel (Pukal)</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialogExcel = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pt-6">
        <v-alert type="info" text dense class="mb-4">
          Sila pilih Tahun, Pusingan dan Negeri untuk memuat turun templat terlebih dahulu.
        </v-alert>

        <!-- DOWNLOAD SECTION -->
        <div class="pa-4 rounded mb-5" style="background-color: #f4f6f8; border: 1px solid #e2e8f0;">
          <div class="subtitle-2 grey--text text--darken-2 mb-3"><v-icon small left color="grey darken-1">mdi-tray-arrow-down</v-icon>1. MUAT TURUN TEMPLAT</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-radio-group v-model='selectedType' row class="mt-0" hide-details>
                <v-radio value="I" label="Pendapatan"></v-radio>
                <v-radio value="E" label="Perbelanjaan"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="selectedTahun" placeholder="Tahun" :items="uniqueTahun" outlined dense hide-details class="white"></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="selectedPusingan" placeholder="Pusingan" :items="uniquePusinganNoSemua" outlined dense hide-details class="white"></v-select>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" md="8">
              <v-autocomplete outlined dense placeholder="Negeri" :items="listNegeriWithAll" :item-text="item => item.negeriID === 'ALL' ? item.negeri : (String(item.negeriID).padStart(2, '0') + ' - ' + item.negeri)" return-object v-model="selectedNegeri" hide-details class="white"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn class="btn-teal text-none" height="40" @click="downloadTemplate" block :disabled="!selectedTahun || !selectedPusingan || !selectedNegeri || !selectedType">
                <v-icon left>mdi-download</v-icon> Muat Turun Templat
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- UPLOAD SECTION -->
        <div class="pa-4 rounded" style="background-color: #f4f6f8; border: 1px solid #e2e8f0;">
          <div class="subtitle-2 grey--text text--darken-2 mb-3"><v-icon small left color="grey darken-1">mdi-tray-arrow-up</v-icon>2. MUAT NAIK DATA</div>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-file-input 
                v-model="excelFile" 
                accept=".xlsx, .xls" 
                placeholder="Pilih Fail Excel..." 
                outlined 
                dense 
                prepend-icon=""
                prepend-inner-icon="mdi-file-excel" 
                color="teal"
                class="white teal-outline"
                hide-details="auto"
                clearable 
                persistent-hint 
                hint="Fail akan dikosongkan selepas muat naik (berjaya atau gagal) untuk mengelakkan ralat 'cache'. Sila pilih semula fail jika anda membuat perubahan di dalam Excel."
              >
                <template v-slot:selection="{ text }">
                  <v-chip small color="teal" dark label class="font-weight-medium">
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn class="btn-teal text-none" height="40" @click="uploadExcel" block :disabled="!excelFile" :loading="isUploading">
                <v-icon left>mdi-upload</v-icon> Muat Naik & Simpan
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- DIALOG JANA TAHUN BARU -->
  <v-dialog v-model="dialogJanaTahun" max-width="450" persistent>
    <v-card>
      <v-card-title class="py-3 kad__tajuk">
        <v-icon small class="mr-2">mdi-calendar-plus</v-icon>
        <span class="subtitle-1 font-weight-medium">Jana Wajaran Tahun Baru</span>
      </v-card-title>
      <v-card-text class="pt-4">
        Sila masukkan tahun yang ingin dijana (contoh: 2025). 
        Tindakan ini akan menjana rekod kosong untuk semua Daerah, Strata, dan Warganegara bagi kesemua 12 Pusingan.
        <br><br>
        <i>* Nilai lalai (default) Wajaran adalah 0 untuk Jenis Pendapatan bagi Bukan Warganegara.</i>
        <v-text-field
          v-model="janaTahunInput"
          label="Tahun"
          outlined
          dense
          type="number"
          class="mt-4"
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text class="text-none" @click="dialogJanaTahun = false" :disabled="isGenerating">Batal</v-btn>
        <v-btn class="btn-teal text-none" @click="janaTahunBaru(false)" :loading="isGenerating" :disabled="!janaTahunInput">Jana</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- DIALOG OVERWRITE TAHUN -->
  <v-dialog v-model="dialogOverwrite" max-width="450" persistent>
    <v-card>
      <v-card-title class="py-3 kad__tajuk kad__tajuk--amaran">
        <v-icon small class="mr-2">mdi-alert</v-icon>
        <span class="subtitle-1 font-weight-medium">Pengesahan Jana Semula</span>
      </v-card-title>
      <v-card-text class="pt-4">
        <strong>Tahun {{ janaTahunInput }} sudah wujud dalam pangkalan data.</strong><br><br>
        Adakah anda pasti mahu memadam semua rekod Wajaran bagi tahun {{ janaTahunInput }} dan menjana semula templat kosong yang baharu? Tindakan ini tidak boleh dibatalkan.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text class="text-none" @click="dialogOverwrite = false" :disabled="isGenerating">Batal</v-btn>
        <v-btn class="btn-danger text-none" @click="janaTahunBaru(true)" :loading="isGenerating">Ya, Teruskan</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- DIALOG SUCCESS -->
  <v-dialog v-model="successDialog" max-width="500">
    <v-card>
      <v-card-title class="py-3 kad__tajuk">
        <v-icon small class="mr-2">mdi-check-circle</v-icon>
        <span class="subtitle-1 font-weight-medium">Berjaya</span>
      </v-card-title>
      <v-card-text class="pt-4 text-center">
        <v-icon color="#037769" size="64">mdi-check-circle</v-icon>
        <div class="mt-4 text-h6">{{ successMessage }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn class="btn-teal text-none" @click="successDialog = false">Tutup</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- DIALOG ERROR -->
  <v-dialog v-model="errorDialog" max-width="500">
    <v-card>
      <v-card-title class="py-3 kad__tajuk kad__tajuk--ralat">
        <v-icon small class="mr-2">mdi-alert-circle</v-icon>
        <span class="subtitle-1 font-weight-medium">Ralat</span>
      </v-card-title>
      <v-card-text class="pt-4 text-center">
        <v-icon color="#d9534f" size="64">mdi-alert-circle</v-icon>
        <div class="mt-4 text-h6">{{ errMessage }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text class="text-none" @click="errorDialog = false">Tutup</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</div>
</template>
<script>
import axios from '../../plugins/axios'
import WajaranSyncButton from './WajaranSyncButton.vue'
// import services
import getLocalStorage from '@/services/getLocalStorage'
const localStorageData = new getLocalStorage(); 

import hidServices from "@/services/lookupField";
const lookup = new hidServices();

import lookupField from '@/services/lookupField'
const lookupFieldServices = new lookupField();

import getPusingan from "@/services/pusingan";
const pusinganServices = new getPusingan();
// end import services

export default {
    components: { WajaranSyncButton },

    async beforeMount(){
      var listNegeri = await lookupFieldServices.getNegeri();
      this.listNegeri = listNegeri.data.data.filter(n => parseInt(n.negeriID) <= 16);

      var listDP = await lookupFieldServices.getDPAll();
      if (listDP && listDP.data) {
        this.listDP = listDP.data;
      }

      try {
        var pusinganList = await pusinganServices.getAllPusingan();
        if (pusinganList && pusinganList.data && pusinganList.data.data) {
          this.dataTapisPusingan = pusinganList.data.data;
          
          if (this.uniqueTahun.length > 0) {
            this.filterTahun = this.uniqueTahun[0];
          }
          
          let semuaPus = this.uniquePusingan.find(p => String(p).includes('(Semua)'));
          if (semuaPus) {
            this.filterPusingan = semuaPus;
          } else if (this.uniquePusingan.length > 0) {
            this.filterPusingan = this.uniquePusingan[0];
          }
        }
      } catch (e) {
        console.error('Error fetching pusingan:', e);
      }

      this.getData();
    },
    computed: {
      headers() {
        return this.defaultHeaders.filter(h => this.selectedHeaders.includes(h.value));
      },
      filteredDataWajaran() {
        return this.dataWajaran.filter(item => {
          let matchTahun = !this.filterTahun || item.tahun == this.filterTahun;
          let matchJenis = !this.filterJenis || item.Type == this.filterJenis;
          
          let matchPusingan = false;
          if (!this.filterPusingan || String(this.filterPusingan).includes('(Semua)')) {
             matchPusingan = true;
          } else {
             matchPusingan = item.P == this.filterPusingan;
          }

          let matchNegeri = !this.filterNegeri || item.NG == this.filterNegeri;
          return matchTahun && matchJenis && matchPusingan && matchNegeri;
        });
      },
      uniqueTahun() {
        if (!this.dataTapisPusingan) return [];
        return [...new Set(
          this.dataTapisPusingan
            .filter(item => (item.pusingan == '01' || item.pusingan == '1') && (item.status == '1' || item.status == 1))
            .map(item => item.tahun)
        )].filter(Boolean).sort();
      },
      uniquePusingan() {
        if (!this.dataTapisPusingan) return [];

        let active = this.dataTapisPusingan;
        
        // Removed filtering by year to correctly show all active Pusingans dynamically
        active = active.filter(item => item.status == '1' || item.status == 1);
        
        let pusingans = [...new Set(active.map(item => item.pusingan))].filter(Boolean).sort();
        
        if (pusingans.length > 1) {
          let min = parseInt(pusingans[0]);
          let max = parseInt(pusingans[pusingans.length - 1]);
          pusingans.push(`${min}-${max} (Semua)`);
        }
        
        return pusingans;
      },
      uniquePusinganNoSemua() {
        if (!this.dataTapisPusingan) return [];

        let active = this.dataTapisPusingan;
        active = active.filter(item => item.status == '1' || item.status == 1);
        return [...new Set(active.map(item => item.pusingan))].filter(Boolean).sort();
      },

      summaryMatrixData() {
        if (!this.filterTahun || !this.filterJenis || !this.listNegeri || !this.uniquePusinganNoSemua) return [];

        let matrix = [];
        for (let negeri of this.listNegeri) {
          if(parseInt(negeri.negeriID) > 16) continue;

          let row = {
            negeriID: negeri.negeriID,
            negeriName: negeri.negeri,
            pusinganData: {}
          };

          for (let pusingan of this.uniquePusinganNoSemua) {
            let pStr = String(parseInt(pusingan)).padStart(2, '0');
            let nId = parseInt(negeri.negeriID);
            let stats = this.preppedSummary?.[this.filterTahun]?.[this.filterJenis]?.[nId]?.[pStr];
            
            // It's completely filled if count > 0 AND nulls == 0
            row.pusinganData[pusingan] = (stats && stats.count > 0 && stats.nulls === 0) ? true : false;
          }
          matrix.push(row);
        }
        return matrix;
      },
      summaryPusinganData() {
        let matrix = this.summaryMatrixData;
        if (!matrix || matrix.length === 0) return {};
        
        let counts = {};
        for (let pusingan of this.uniquePusinganNoSemua) {
          let doneCount = 0;
          for (let row of matrix) {
            if (row.pusinganData[pusingan]) doneCount++;
          }
          counts[pusingan] = doneCount;
        }
        return counts;
      },
      filteredDPList() {
        if (!this.selectedNegeri) return [];
        if (this.selectedNegeri.negeriID === 'ALL') return [];
        let ngId = parseInt(this.selectedNegeri.negeriID);
        return this.listDP.filter(x => parseInt(x.NG) === ngId);
      },
      listNegeriWithAll() {
        return [
          { negeriID: 'ALL', negeri: 'Semua Negeri (Pukal Seluruh Negara)' },
          ...this.listNegeri
        ];
      }
    },
    data: () => ({
        successDialog: false,
        successMessage: '',
        dialogJanaTahun: false,
        dialogOverwrite: false,
        janaTahunInput: '',
        isGenerating: false,
        dialogForm: false,
        dialogExcel: false,
        isEdit: false,
        summaryExpanded: false,
        preppedSummary: {},

        filterTahun: null,
        filterJenis: 'I',
        filterPusingan: null,
        filterNegeri: null,

        selectedType: 'I',
        selectedNegeri: null,
        selectedTahun: '',
        selectedPusingan: '',
        selectedDP: '',
        selectedStrata: '',
        wajaranValue: '',
        selectedKW: '',
        listNegeri: [],
        listDP: [],
        dataWajaran: [],
        dataTapisPusingan: [],
        excelFile: null,
        isUploading: false,
        errorDialog: false,
        errMessage: '',

        selectedHeaders: ['Type', 'tahun', 'P', 'NG', 'DP', 'ST', 'KWKIRB', 'wajaran'],
        defaultHeaders: [
          {text: 'Jenis', value: 'Type'},
          {text: 'Tahun', value: 'tahun'},
          {text: 'Pusingan', value: 'P'},
          {text: 'Negeri', value: 'NG'},
          {text: 'DP', value: 'DP'},
          {text: 'Strata', value: 'ST'},
          {text: 'Warganegara', value: 'KWKIRB'},
          {text: 'Wajaran', value: 'wajaran'},
        ],
        valid: true,
    }),

    methods: {
      getNegeriName(id) {
        if (!this.listNegeri || !id) return id;
        let n = this.listNegeri.find(x => parseInt(x.negeriID) == parseInt(id));
        return n ? `${String(id).padStart(2, '0')} - ${n.negeri}` : id;
      },
      getDPName(ng, dpId) {
        if (!this.listDP || !dpId || !ng) return dpId;
        let n = this.listDP.find(x => parseInt(x.DP) == parseInt(dpId) && parseInt(x.NG) == parseInt(ng));
        return n ? `${String(dpId).padStart(2, '0')} - ${n.NamaDP}` : dpId;
      },
      openFormDialog(mode) {
        if (mode === 'tambah') {
          this.isEdit = false;
          this.reset();
        } else {
          this.isEdit = true;
        }
        this.dialogForm = true;
      },
      openExcelDialog() {
        this.reset();
        this.dialogExcel = true;
      },
      resetFiltersList() {
        this.filterJenis = 'I';
        this.filterTahun = this.uniqueTahun && this.uniqueTahun.length > 0 ? this.uniqueTahun[0] : '';
        let semuaPus = this.uniquePusingan.find(p => String(p).includes('(Semua)'));
        this.filterPusingan = semuaPus ? semuaPus : (this.uniquePusingan.length > 0 ? this.uniquePusingan[0] : '');
        this.filterNegeri = null;
      },
      reset() {
        if(this.$refs.form) this.$refs.form.resetValidation();
        this.selectedType = 'I';
        this.selectedNegeri = null;
        this.selectedTahun = this.uniqueTahun && this.uniqueTahun.length > 0 ? this.uniqueTahun[0] : '';
        this.selectedPusingan = '';
        this.selectedDP = '';
        this.selectedStrata = '';
        this.selectedKW = '';
        this.wajaranValue = '';
        this.excelFile = null;
      },

      async downloadTemplate() {
        try {
          const Type = this.selectedType;
          const tahun = this.selectedTahun;
          const P = this.selectedPusingan;
          const NG = this.selectedNegeri.negeriID;

          const response = await axios.get(`${process.env.VUE_APP_API_URL}/JadualAwalan/downloadWajaranTemplate?Type=${Type}&tahun=${tahun}&P=${P}&NG=${NG}`, {
            responseType: 'blob'
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Template_Wajaran_Type${Type}_T${tahun}_P${P}_NG${NG}.xlsx`);
          document.body.appendChild(link);
          link.click();
        } catch (error) {
          this.errorDialog = true;
          this.errMessage = 'Gagal muat turun templat';
        }
      },

      async uploadExcel() {
        if (!this.excelFile) return;
        this.isUploading = true;
        try {
          let formData = new FormData();
          formData.append('file', this.excelFile);

          const res = await axios.post(`${process.env.VUE_APP_API_URL}/JadualAwalan/uploadWajaran`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          this.successMessage = 'Data Pukal Berjaya Disimpan!';
          this.successDialog = true;
          this.excelFile = null;
          this.dialogExcel = false;
          this.getData();
        } catch (error) {
          this.errorDialog = true;
          this.errMessage = error.response && error.response.data && error.response.data.error 
            ? error.response.data.error 
            : 'Gagal muat naik excel';
        } finally {
          this.isUploading = false;
          this.excelFile = null;
        }
      },

      prepSummaryData() {
        let prep = {};
        for (let w of this.dataWajaran) {
          let t = w.tahun;
          let j = w.Type;
          let n = parseInt(w.NG);
          let p = parseInt(w.P).toString().padStart(2, '0');

          if (!prep[t]) prep[t] = {};
          if (!prep[t][j]) prep[t][j] = {};
          if (!prep[t][j][n]) prep[t][j][n] = {};
          if (!prep[t][j][n][p]) {
            prep[t][j][n][p] = { count: 0, nulls: 0 };
          }
          
          prep[t][j][n][p].count++;
          if (w.wajaran === null || w.wajaran === undefined || w.wajaran === '') {
            prep[t][j][n][p].nulls++;
          }
        }
        this.preppedSummary = prep;
      },

      async getData() {
        try {
            const res = await axios.get(`${process.env.VUE_APP_API_URL}/JadualAwalan/getWajaran`);
            this.dataWajaran = res.data.data;
            this.prepSummaryData();
            // Re-check whether a selaras is now needed (enables the button after an edit).
            if (this.$refs.selarasBtn) this.$refs.selarasBtn.refresh();
        } catch (error) {
            this.errorDialog = true;
            this.errMessage = 'getData : '+ error;
        }
      },

      async janaTahunBaru(overwrite = false) {
        if (!this.janaTahunInput) return;
        this.isGenerating = true;
        
        try {
          const res = await axios.post(`${process.env.VUE_APP_API_URL}/wajaran/generateWajaranTahun`, {
            tahun: this.janaTahunInput,
            overwrite: overwrite
          });
          
          this.isGenerating = false;
          this.dialogJanaTahun = false;
          this.dialogOverwrite = false;
          this.janaTahunInput = '';
          
          this.successMessage = res.data.message || 'Tahun berjaya dijana.';
          this.successDialog = true;
          
          // Refresh table data
          this.getData();
          
        } catch (error) {
          this.isGenerating = false;
          
          if (error.response && error.response.status === 409 && error.response.data.needsConfirmation) {
            this.dialogJanaTahun = false; // Hide first dialog
            this.dialogOverwrite = true;  // Show confirmation dialog
          } else {
            this.dialogJanaTahun = false;
            this.dialogOverwrite = false;
            this.errorDialog = true;
            this.errMessage = (error.response && error.response.data.error) ? error.response.data.error : error.message;
          }
        }
      },

      async simpanWajaranPendapatan() {
        if(this.$refs.form.validate()) {
          if (!this.isEdit) {
            let ngId = this.selectedNegeri ? this.selectedNegeri.negeriID : '';
            let isDuplicate = this.dataWajaran.some(item => {
              return item.Type == this.selectedType &&
                     item.tahun == this.selectedTahun &&
                     parseInt(item.P) == parseInt(this.selectedPusingan) &&
                     parseInt(item.NG) == parseInt(ngId) &&
                     parseInt(item.DP) == parseInt(this.selectedDP) &&
                     item.ST == this.selectedStrata &&
                     item.KWKIRB == this.selectedKW;
            });
            if (isDuplicate) {
              this.errorDialog = true;
              this.errMessage = 'Rekod dengan kriteria yang sama telah wujud.';
              return;
            }
          }

          try {
              const res = await axios.post(`${process.env.VUE_APP_API_URL}/JadualAwalan/simpanWajaranPendapatan`, {
                Type: this.selectedType,
                tahun: this.selectedTahun,
                P: this.selectedPusingan,
                NG: this.selectedNegeri ? this.selectedNegeri.negeriID : '',
                DP: this.selectedDP,
                ST: this.selectedStrata,
                wajaran: this.wajaranValue,
                KWKIRB: this.selectedKW
              });
              
              this.successMessage = 'Data Disimpan!';
              this.successDialog = true;
              this.dialogForm = false;
              this.getData();
              this.reset();
          } catch (error) {
              this.errorDialog = true;
              this.errMessage = 'Simpan : '+error;
          }
        }
      },

      handleClick(row) {
          this.openFormDialog('kemaskini');
          this.selectedType = String(row.Type);
          this.selectedTahun = row.tahun;
          this.selectedPusingan = row.P;
          this.selectedDP = row.DP;
          this.selectedStrata = row.ST;
          this.wajaranValue = row.wajaran;
          this.selectedKW = row.KWKIRB;

          if (this.listNegeri && this.listNegeri.length > 0) {
            for(var i = 0; i < this.listNegeri.length; i++){
                if(row.NG == this.listNegeri[i].negeriID){
                  this.selectedNegeri = this.listNegeri[i];
                  break;
                }
            }
          }
      },
    }
}
</script>

<style>
/* ─── Filter bar — matches the Jadual Awalan / Kadar Respon filter theme ─── */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}

/* Label penapis — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.wajaran-page .fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}

/* Medan outlined: birai teal lembut, menyerlah teal-700 bila fokus. */
.wajaran-page .filter-bar .v-input fieldset {
  border-color: #cfeae7;
}
.wajaran-page .filter-bar .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.wajaran-page .filter-bar .v-select__selection--comma {
  color: #21403c;
}
.wajaran-page .filter-bar .v-input__append-inner .v-icon {
  color: #7aa8a2;
}

/* Kepala kad & dialog — jalur putih dengan ikon teal. Dahulu SETIAP dialog
   memakai jalur warna pejal yang berlainan (primary/success/info/warning/error)
   dengan kelas `headline` 24px: lima gaya kepala pada satu halaman, dan tiada
   satu pun sepadan dengan kad di halaman lain.

   TIDAK berskop pada `.wajaran-page`: Vuetify memindahkan kandungan v-dialog ke
   akar aplikasi, di LUAR elemen halaman — pemilih berskop halaman tidak akan
   mengenainya. Itulah sebabnya <style> fail ini juga tidak `scoped`. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}
/* Amaran & ralat kekal berwarna — di sinilah warna memang membawa makna. */
.kad__tajuk--amaran { border-bottom-color: #ffe082; }
.kad__tajuk--amaran .v-icon { color: #b98900; }
.kad__tajuk--ralat { border-bottom-color: #f3c9c7; }
.kad__tajuk--ralat .v-icon { color: #d9534f; }

/* Butang teal — menggantikan primary/success/info Vuetify. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }

/* Merah untuk tindakan memusnahkan (jana semula menimpa data tahun sedia ada). */
.btn-danger.v-btn {
  background: #d9534f !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-danger.v-btn:hover { background: #b3312d !important; }

/* Medan dalam dialog: birai teal yang sama seperti bar penapis. */
.v-dialog .v-input fieldset { border-color: #cfeae7; }
.v-dialog .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}

/* Nota panduan di atas jadual. */
.wajaran-page .panduan-klik {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f7fcfb;
  border: 1px solid #dbeeeb;
  border-radius: 4px;
  font-size: var(--fs-sm);
  color: #4a8a84;
}
.wajaran-page .panduan-klik__ikon { color: #037769 !important; }

/* Kursor menunjuk mengesahkan nota di atas: baris ini memang boleh diklik. */
.wajaran-page .wajaran-table tbody tr { cursor: pointer; }
.wajaran-page .wajaran-table tbody tr:hover { background: #eef8f6 !important; }

/* Butang "Tetapkan Semula" — senada teal. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}
.reset-btn.v-btn--outlined .v-icon { color: #037769; }

/* Custom teal outline for file upload */
.teal-outline >>> fieldset { border-color: #009688 !important; border-width: 2px !important; }
.teal-outline:hover >>> fieldset { border-color: #00796B !important; }
</style>
